/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { WebsocketTypes } from '../api/enumerations';

/**
 * Websocket events by topic.
 *
 * The socket lives in the Menu (one connection per session). Every message is
 * parsed once here and delivered only to the components that subscribed to
 * its `type`. Subscribers are kept in a ref, so publishing never changes the
 * context value and never re-renders the tree — before, each message
 * re-rendered every consumer and each Home card parsed it again.
 */
export interface WebsocketEvent<P = any> {
  type: WebsocketTypes | string;
  payload: P;
  raw: MessageEvent;
}

type Handler = (event: WebsocketEvent) => void;

interface Subscription {
  types: ReadonlySet<string> | null;
  handler: Handler;
}

interface WebsocketContextValue {
  /** Feed a raw socket message; parses and fans out to subscribers. */
  publish: (message: MessageEvent) => void;
  /** Subscribe to some types (or every type with `null`); returns unsubscribe. */
  subscribe: (types: string[] | null, handler: Handler) => () => void;
}

export const WebsocketContext = createContext<WebsocketContextValue>({
  publish: () => undefined,
  subscribe: () => () => undefined,
});

const parseMessage = (message: MessageEvent): WebsocketEvent | null => {
  try {
    const parsed = JSON.parse(message.data);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.type !== 'string'
    ) {
      return null;
    }
    return { type: parsed.type, payload: parsed.payload, raw: message };
  } catch {
    return null;
  }
};

interface WebsocketProviderProps {
  children: ReactNode;
}

export function WebsocketProvider({
  children,
}: WebsocketProviderProps): JSX.Element {
  const subscriptions = useRef(new Set<Subscription>());

  const publish = useCallback((message: MessageEvent): void => {
    const event = parseMessage(message);
    if (!event) return;
    subscriptions.current.forEach((subscription) => {
      if (!subscription.types || subscription.types.has(event.type)) {
        subscription.handler(event);
      }
    });
  }, []);

  const subscribe = useCallback(
    (types: string[] | null, handler: Handler): (() => void) => {
      const subscription: Subscription = {
        types: types ? new Set(types) : null,
        handler,
      };
      subscriptions.current.add(subscription);
      return () => {
        subscriptions.current.delete(subscription);
      };
    },
    []
  );

  const value = useMemo(() => ({ publish, subscribe }), [publish, subscribe]);

  return (
    <WebsocketContext.Provider value={value}>
      {children}
    </WebsocketContext.Provider>
  );
}

/** Runs `handler` for every socket event of the given type(s). Always the latest handler. */
export function useWebsocketEvent<P = any>(
  types: WebsocketTypes | WebsocketTypes[],
  handler: (event: WebsocketEvent<P>) => void
): void {
  const { subscribe } = useContext(WebsocketContext);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const key = Array.isArray(types) ? types.join('|') : types;

  useEffect(
    () =>
      subscribe(key.split('|'), (event) =>
        handlerRef.current(event as WebsocketEvent<P>)
      ),
    [subscribe, key]
  );
}

/** The socket owner calls this with each raw message. */
export function useWebsocketPublisher(): (message: MessageEvent) => void {
  return useContext(WebsocketContext).publish;
}
